import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

/** One deposit-bearing order from GET /admin/deposits on the new (ECS)
 *  backend — contract in docs/plans/deposit-stripe-hold-runtime-spec.md §4.
 *  `depositStatus` is one of: pending, authorized, capture_failed,
 *  captured_full, captured_partial, released, expired, failed. Compare
 *  case-insensitively. */
export interface AdminDeposit {
    orderId: string;
    orderNumber: string;
    groupName: string;
    /** Null/omitted when the order is anonymized. */
    customerName?: string | null;
    depositAmount: number;
    depositStatus: string;
    authorizedAt: string | null;
    authExpiresAt: string | null;
    capturedAmount: number | null;
    capturedAt: string | null;
    captureReason: string | null;
    releasedAt: string | null;
    /** Overtime fee already billed separately — shown next to the capture
     *  form so staff never capture it from the deposit again (B9). */
    overtimeFee: number;
}

export interface DepositListResponse {
    items: AdminDeposit[];
    total: number;
}

export interface GetDepositsArgs {
    /** Enum values for the comma-separated `status` param; omitted = all. */
    statuses?: string[];
    /** 0-based (first page = 0), per spec §4. */
    page?: number;
    pageSize?: number;
}

/** Body for POST /admin/deposits/:orderId/release — reason is optional
 *  free text (accepted and discarded by the backend in v1). */
export interface ReleaseDepositArgs {
    orderId: string;
    reason: string | null;
}

/** Body for POST /admin/deposits/:orderId/capture — both fields required;
 *  0 < amount <= depositAmount, max two decimals. */
export interface CaptureDepositArgs {
    orderId: string;
    amount: number;
    reason: string;
}

// Deposit ops endpoints on the new (ECS) backend. Release/capture move real
// money and are gated server-side on the `manage:deposits` permission (403
// without it); the list is readable by any tenant admin.
export const depositAdminApiSlice = createApi({
    reducerPath: 'depositAdminApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['Deposits'],
    endpoints: (builder) => ({
        // Orders with deposit_status != not_required, newest first.
        getDeposits: builder.query<DepositListResponse, GetDepositsArgs>({
            query: ({ statuses, page, pageSize }) => {
                const params = new URLSearchParams();
                if (statuses && statuses.length > 0) params.set('status', statuses.join(','));
                if (page != null) params.set('page', String(page));
                if (pageSize != null) params.set('pageSize', String(pageSize));
                const search = params.toString();
                return {
                    url: `/deposits${search ? `?${search}` : ''}`,
                    method: 'GET',
                };
            },
            providesTags: ['Deposits'],
        }),
        // Cancel the hold (allowed while authorized / capture_failed, else 409).
        releaseDeposit: builder.mutation<void, ReleaseDepositArgs>({
            query: ({ orderId, reason }) => ({
                url: `/deposits/${encodeURIComponent(orderId)}/release`,
                method: 'POST',
                data: { reason },
            }),
            invalidatesTags: ['Deposits'],
        }),
        // Capture part or all of the hold (same precondition, else 409).
        captureDeposit: builder.mutation<void, CaptureDepositArgs>({
            query: ({ orderId, amount, reason }) => ({
                url: `/deposits/${encodeURIComponent(orderId)}/capture`,
                method: 'POST',
                data: { amount, reason },
            }),
            invalidatesTags: ['Deposits'],
        }),
    }),
});

export const {
    useGetDepositsQuery,
    useReleaseDepositMutation,
    useCaptureDepositMutation,
} = depositAdminApiSlice;
